import { Link } from 'gatsby';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import React, { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import showdown from 'showdown';
import DefaultImage from '../../../../../static/default-cover-image.jpg';
import { formatDateWithi18n } from '../../../../helpers/Formater';
import { formatSlug } from '../../../../utils/utils';
import type { MainPostProps } from './types';

const MainPostSection: FC<MainPostProps> = ({ edges }) => {
    const { t } = useTranslation();

    const featured = edges[0]?.node;
    const source = featured.header_image.CloudinaryImageFromStrapi;
    const imageData = getImage(source);

    const convertor = new showdown.Converter({
        disableForced4SpacesIndentedSublists: true,
    });

    const getContent = () => {
        const plaintext = convertor.makeHtml(featured.content.data.content).replace(/<[^>]*>/g, '');

        if (plaintext.length > 250) {
            const hasSpace = plaintext[249] === ' ' ? 1 : 0; // check if the last word is an empty space
            return `${plaintext.substring(0, 250 - hasSpace)}...`;
        }

        return plaintext;
    };

    return (
        <div className="group mt-10">
            {featured && (
                <Link className="main-post-title" to={`/${formatSlug(featured.slug, featured.locale)}`}>
                    <div className="flex flex-wrap hover:shadow-lg">
                        <div className="overflow-hidden w-full bg-contain bg-no-repeat bg-center md:w-1/2">
                            {source ? (
                                <GatsbyImage
                                    loading="eager"
                                    image={imageData}
                                    alt={featured.title}
                                    className="object-cover w-full h-full group-hover:scale-125 transition-all duration-500"
                                />
                            ) : (
                                <img
                                    className="object-cover w-full h-full group-hover:scale-125 transition-all duration-500"
                                    src={DefaultImage}
                                    alt={featured.title}
                                />
                            )}
                        </div>
                        <div className="h-[calc(100% - 50px)] bg-white p-6 md:w-1/2">
                            <h4 className="text-3xl mb-8">{featured.title}</h4>
                            <p className="leading-normal my-4 text-[#888]">{getContent()}</p>
                            <p className="font-normal text-gray-400">
                                {formatDateWithi18n({ value: featured.publishedAt })}
                                <span>
                                    {` ${featured.authors.length ? t('general.by', 'by') : ''}`}
                                    {featured.authors.map((author) => ` ${author.first_name} ${author.last_name}`).join(', ')}
                                </span>
                            </p>
                        </div>
                    </div>
                </Link>
            )}
        </div>
    );
};

export default MainPostSection;
